import React from 'react';
import 'slick-carousel/slick/slick.css';
import Carousel from '@narvar/nth-bundle-carousel';
import {
  NarvarClickSubTypes,
  NarvarClickTypes,
  NarvarEventComponents,
  NarvarEventTypes,
  useAnalyticsPost,
} from '@narvar/nth-hook-context-analytics';
import SliderTitle from '../SliderTitle';
import ProductTile from '../ProductTile';
import TileParts from '../TileParts';
import { useCustomComponentStateContext } from '../../hooks/useCustomComponentState';
import useProductRecsApi, { MappedItem } from '../../hooks/useProductRecsApi';
import useIsMLProductRecsOn from '../../hooks/useIsMLProductRecsOn';

type ResponsiveBreakpointSettings = {
  breakpoint: number;
  settings: {
    dots: boolean;
    slidesToScroll: number;
    slidesToShow: number;
  };
};

interface CarouselSettings {
  dots?: boolean;
  infinite?: boolean;
  responsive?: ResponsiveBreakpointSettings[];
  slidesToScroll?: number;
  slidesToShow: number;
}

function CustomCarousel() {
  const { state } = useCustomComponentStateContext();
  const logEvent = useAnalyticsPost();

  const retailerTheme = state?.queryData?.retailerTheme;

  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  const productrecsarrowcontainerNextNone: React.CSSProperties =
    typeof retailerTheme?.productrecsarrowcontainerNextNone === 'object'
      ? retailerTheme?.productrecsarrowcontainerNextNone
      : {};
  const productrecsarrowcontainerPrevNone: React.CSSProperties =
    typeof retailerTheme?.productrecsarrowcontainerPrevNone === 'object'
      ? retailerTheme?.productrecsarrowcontainerPrevNone
      : {};
  const productrecsarrowNoneDefault: React.CSSProperties =
    typeof retailerTheme?.productrecsarrowNoneDefault === 'object'
      ? retailerTheme?.productrecsarrowNoneDefault
      : {};
  const productrecsarrowNoneDisabled: React.CSSProperties =
    typeof retailerTheme?.productrecsarrowNoneDisabled === 'object'
      ? retailerTheme?.productrecsarrowNoneDisabled
      : {};
  const productrecsarrowNoneHover: React.CSSProperties =
    typeof retailerTheme?.productrecsarrowNoneHover === 'object'
      ? retailerTheme?.productrecsarrowNoneHover
      : {};
  const productrecspanelcontainerNoneNone: React.CSSProperties =
    typeof retailerTheme?.productrecspanelcontainerNoneNone === 'object'
      ? retailerTheme?.productrecspanelcontainerNoneNone
      : {};
  const productrecsdotcontainerNoneNone: React.CSSProperties =
    typeof retailerTheme?.productrecsdotcontainerNoneNone === 'object'
      ? retailerTheme?.productrecsdotcontainerNoneNone
      : {};
  // TODO: Add `productrecscontainerNoneNone` theme token
  // const productrecscontainerNoneNone: unknown =
  //   retailerTheme?.productrecscontainerNoneNone || panelcontainerNoneNone || {};

  const productRecommendations =
    (state?.queryData?.retailerSettings?.productRecommendations?.type !==
      'SCRIPT' &&
      state?.queryData?.retailerSettings?.productRecommendations) ||
    {};

  const {
    carouselSettings: slickOptions,
  }: { carouselSettings?: CarouselSettings } = productRecommendations;

  const mlProductRecsEnabled = useIsMLProductRecsOn('track-npr');
  const mappedItemData = useProductRecsApi({ mlProductRecsEnabled });

  if (!mappedItemData || !mappedItemData.length) return null;
  const itemsCount = mappedItemData.length;

  if (slickOptions && slickOptions.responsive) {
    // handles bug in Slick that adds duplicate rows if slidesToShow > itemsCount
    slickOptions.responsive.forEach(
      (breakpoint: ResponsiveBreakpointSettings) => {
        const maxSlides = Math.min(
          breakpoint.settings.slidesToShow,
          itemsCount,
        );
        // eslint-disable-next-line no-param-reassign
        breakpoint.settings.slidesToShow = maxSlides;
      },
    );
  }

  return (
    <div
      style={{
        ...(typeof productrecspanelcontainerNoneNone === 'object'
          ? productrecspanelcontainerNoneNone
          : {}),
        padding: '1.5rem 2rem 3rem 2rem',
      }}
    >
      <SliderTitle />
      <Carousel
        slickProps={
          slickOptions && typeof slickOptions === 'object' ? slickOptions : {}
        }
        arrowContainerNextStyles={productrecsarrowcontainerNextNone || {}}
        arrowContainerPrevStyles={productrecsarrowcontainerPrevNone || {}}
        arrowDefaultStyles={productrecsarrowNoneDefault}
        arrowDisabledStyles={productrecsarrowNoneDisabled}
        arrowActiveStyles={productrecsarrowNoneHover}
        dotContainerStyle={productrecsdotcontainerNoneNone}
        onArrowClick={() => {
          logEvent({
            clickCategory: NarvarClickSubTypes.PRODUCT_RECOMMENDATIONS,
            clickSubType: NarvarClickSubTypes.PRODUCT_RECOMMENDATIONS,
            clickType: NarvarClickTypes.INTERACTION,
            eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
            eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
            eventType: NarvarEventTypes.CLICK,
            feature: 'productRecommendations',
            pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
          });
        }}
        itemsCount={itemsCount}
      >
        {(Array.isArray(mappedItemData) ? mappedItemData : []).map(
          (productInfo: MappedItem, sliderIndex: number) => (
            <div
              key={Object.entries(productInfo).reduce(
                (key, [value]) => `${key}${value}`,
                '',
              )}
              // centers whole product tile inside the wrapper div generated by slick
              style={{ textAlign: 'center' }}
            >
              <ProductTile sliderIndex={sliderIndex} productInfo={productInfo}>
                <TileParts itemInfo={productInfo} />
              </ProductTile>
            </div>
          ),
        )}
      </Carousel>
    </div>
  );
}

export default CustomCarousel;
